export enum AdminRole {
  SUPER_ADMIN = 'super_admin',
  MODERATOR = 'moderator',
  SUPPORT = 'support',
  VIEWER = 'viewer'
}

export interface AdminPermissions {
  users: {
    read: boolean;
    verify: boolean;
    delete: boolean;
    ban: boolean;
  };
  products: {
    read: boolean;
    delete: boolean;
    feature: boolean;
  };
  chats: {
    read: boolean;
    moderate: boolean;
  };
  reports: {
    read: boolean;
    resolve: boolean;
  };
  analytics: {
    read: boolean;
  };
  settings: {
    read: boolean;
    write: boolean;
  };
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  permissions: AdminPermissions;
  createdAt: Date;
  lastLoginAt: Date;
  isActive: boolean;
}

export interface AdminAction {
  id: string;
  adminId: string;
  adminName: string;
  action: string;
  targetType: 'user' | 'product' | 'chat' | 'report' | 'inquiry';
  targetId: string;
  details: string;
  timestamp: Date;
  ipAddress: string;
}

export interface SystemStats {
  date: string;
  totalUsers: number;
  newUsers: number;
  totalProducts: number;
  newProducts: number;
  totalTransactions: number;
  totalRevenue: number;
  pendingReports: number;
  pendingInquiries: number;
  pendingVerifications: number;
  pendingPayments: number;
  pendingBankTransfers: number;
  pendingOtherPayments: number;
  tutorialStats?: {
    sell: number;
    buy: number;
    total: number;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  firebaseToken?: string;
  user: AdminUser;
}

export interface DashboardStatsResponse {
  overview: SystemStats;
  charts: {
    userGrowth: ChartData[];
    productGrowth: ChartData[];
    transactionVolume: ChartData[];
  };
  alerts: Alert[];
  recentActions: AdminAction[];
}

export interface ChartData {
  date: string;
  value: number;
}

export interface Alert {
  id: string;
  type: 'error' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  createdAt: Date;
  isRead: boolean;
}

// 設定ページ用の型定義
export interface SystemSettings {
  id: string;
  appName: string;
  appDescription: string;
  notificationSettings: {
    emailEnabled: boolean;
    pushEnabled: boolean;
    adminNotificationEmail: string;
  };
  reviewSettings: {
    autoReviewEnabled: boolean;
    reviewThreshold: number;
    requireManualReview: boolean;
  };
  feeSettings: {
    transactionFeeRate: number;
    paymentFeeRate: number;
    minimumFee: number;
  };
  termsOfService: string;
  privacyPolicy: string;
  updatedAt: Date;
  updatedBy: string;
}

export interface AdminSettings {
  id: string;
  securitySettings: {
    jwtExpirationDays: number;
    allowedIPs: string[];
    maxLoginAttempts: number;
    sessionTimeoutMinutes: number;
  };
  generalSettings: {
    language: 'ja' | 'en';
    timezone: string;
    dateFormat: string;
    itemsPerPage: number;
  };
  maintenanceSettings: {
    isMaintenanceMode: boolean;
    maintenanceMessage: string;
    maintenanceSchedule?: Date;
  };
  updatedAt: Date;
  updatedBy: string;
}

export interface CreateAdminRequest {
  email: string;
  name: string;
  password: string;
  role: AdminRole;
  permissions: AdminPermissions;
}

export interface UpdateAdminRequest {
  name?: string;
  role?: AdminRole;
  permissions?: AdminPermissions;
  isActive?: boolean;
}

export interface SystemSettingsRequest {
  appName: string;
  appDescription: string;
  notificationSettings: {
    emailEnabled: boolean;
    pushEnabled: boolean;
    adminNotificationEmail: string;
  };
  reviewSettings: {
    autoReviewEnabled: boolean;
    reviewThreshold: number;
    requireManualReview: boolean;
  };
  feeSettings: {
    transactionFeeRate: number;
    paymentFeeRate: number;
    minimumFee: number;
  };
  termsOfService: string;
  privacyPolicy: string;
}

export interface AdminSettingsRequest {
  securitySettings: {
    jwtExpirationDays: number;
    allowedIPs: string[];
    maxLoginAttempts: number;
    sessionTimeoutMinutes: number;
  };
  generalSettings: {
    language: 'ja' | 'en';
    timezone: string;
    dateFormat: string;
    itemsPerPage: number;
  };
  maintenanceSettings: {
    isMaintenanceMode: boolean;
    maintenanceMessage: string;
    maintenanceSchedule?: Date;
  };
} 